import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import posthog from 'posthog-js';

const propTypes = {
  imgsAndLinks: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })
  ).isRequired,
};

// Ad-blocker evasion notes — read before "fixing" anything here:
// Filter lists block by keyword in REQUEST URLS and DOM SELECTORS only.
// - Image URLs must stay under the 'bainers/' storage path (see assetsMap.js).
//   That deliberate misspelling contains neither "banner" nor "ad".
// - This component must keep using inline styles: a CSS-module class like
//   bannerAds_banner__hash would land in the DOM and get hidden by
//   cosmetic rules such as [class*="banner"].
// - Keep alt text free of "ad"/"banner" words (e.g. don't use "Advertisement"),
//   some lists match img[alt*=...].
// File and component names never reach the DOM or a request URL, so they
// are named honestly.
export default function BannerAds({ imgsAndLinks }) {
  if (!imgsAndLinks.length) return null;
  const [imgIndex, setImgIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Rotate banners using a single interval, pausing while hovered
  useEffect(() => {
    const intervalId = setInterval(() => {
      setImgIndex((prevIndex) =>
        isHovered ? prevIndex : prevIndex === imgsAndLinks.length - 1 ? 0 : prevIndex + 1
      );
    }, 30000);

    return () => clearInterval(intervalId);
  }, [imgsAndLinks.length, isHovered]);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '910px',
        margin: '0 auto 16px',
      }}
    >
      <div style={{ visibility: 'hidden' }}>
        <img
          src={imgsAndLinks[0].src}
          alt=""
          aria-hidden="true"
          style={{ width: '100%', display: 'block' }}
        />
      </div>
      {imgsAndLinks.map((img, index) => (
        <a
          key={img.src}
          href={img.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => posthog.capture('banner_ad_click', { href: img.href, alt: img.alt })}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            opacity: index === imgIndex ? 1 : 0,
            pointerEvents: index === imgIndex ? 'auto' : 'none',
            transition: 'opacity 0.4s ease-in-out, transform 0.4s ease-out',
            transform: index === imgIndex ? 'translateY(0)' : 'translateY(4px)',
          }}
        >
          <img src={img.src} alt={img.alt} style={{ width: '100%', display: 'block' }} />
        </a>
      ))}
    </div>
  );
}

BannerAds.propTypes = propTypes;
